import type { Namespace } from '../types';

// Site receiving (R-145, reshaped R-234 P4, rebuilt to the prototype R-302).
// The screen is a QUEUE: every open / partially-received PO, its lines, and a
// Receive button per line that opens the per-line modal. The backend mints one
// asset per physical unit plus a receiving slip. Offline-idempotent via client_ref.
export const receiving = {
  title: { de: 'Wareneingang', en: 'Receiving' },
  desc: {
    de: 'Wareneingang. Jede angenommene Einheit wird ein Asset — ein Etikett aus dem Pool wird ihrer Seriennummer zugeordnet, genau wie bei der manuellen Erfassung, und die Einkaufsdaten kommen aus der Bestellung.',
    en: 'Goods in. Each unit received becomes an asset — a label from the pool is matched to its serial, exactly as at manual intake, and the purchase details are written from the purchase order.',
  },
  navLabel: { de: 'Wareneingang', en: 'Receiving' },

  financialNote: {
    de: 'Der Wareneingang füllt den kaufmännischen Datensatz für Sie. Lieferant, Preis, Bestellung und Wareneingangsbeleg werden auf jedes neue Asset kopiert, damit niemand sie später eintippen muss.',
    en: 'Receiving is what fills in the financial record for you. The supplier, what was paid, the purchase order and the receipt are all copied onto each new asset, so nobody has to type them in later.',
  },

  // ── the queue: one panel per order, one row per line ──
  poSupplier: { de: 'Lieferant', en: 'Supplier' },
  expected: { de: 'erwartet {date}', en: 'expected {date}' },
  colModel: { de: 'Modell', en: 'Model' },
  colClass: { de: 'Klasse', en: 'Class' },
  colOrdered: { de: 'Bestellt', en: 'Ordered' },
  colReceived: { de: 'Erhalten', en: 'Received' },
  colOutstanding: { de: 'Offen', en: 'Outstanding' },
  colLabels: { de: 'Etiketten verfügbar', en: 'Labels available' },
  labelsInPool: { de: '{count} im Pool', en: '{count} in pool' },
  labelsNeed: { de: '{have} im Pool — {need} nötig', en: '{have} in pool — need {need}' },
  rowComplete: { de: 'vollständig', en: 'complete' },
  receive: { de: 'Annehmen', en: 'Receive' },
  noLabelHint: {
    de: 'Kein freies Etikett im Pool für diese Klasse',
    en: 'No unused label in the pool for this class',
  },

  // ── the per-line modal ──
  modalTitle: { de: '{model} annehmen', en: 'Receive {model}' },
  mPo: { de: 'Bestellung', en: 'Purchase order' },
  mUnitPrice: { de: 'Einzelpreis', en: 'Unit price' },
  unitCount: { de: '{count} Einheit(en)', en: '{count} unit(s)' },
  qtyLabel: { de: 'Anzunehmende Menge', en: 'Quantity to receive' },
  qtyPh: { de: 'z. B. 5', en: 'e.g. 5' },
  qtyHint: {
    de: 'Bis zu {outstanding} offen und {labels} Etikett(en) im Pool. Teillieferungen sind in Ordnung — die Bestellung bleibt offen.',
    en: 'Up to {outstanding} outstanding and {labels} label(s) in the pool. Partial receipts are fine — the purchase order stays open.',
  },
  qtyRange: { de: 'Zwischen 1 und {max}', en: 'Between 1 and {max}' },
  unitsTitle: { de: 'Einheiten', en: 'Units' },
  unitsHint: {
    de: 'Eine Zeile pro physischer Einheit. Ohne Seriennummer muss ein Grund angegeben werden — dann ist das Etikett die Identität.',
    en: 'One row per physical unit. Leave the serial empty and pick a reason if the unit has none — the tag becomes its identity.',
  },
  colAssetTag: { de: 'Etikett (aus Pool)', en: 'Asset tag (from pool)' },
  serials: { de: 'Seriennummer', en: 'Serial' },
  serialPlaceholder: { de: 'Seriennummer scannen/eingeben…', en: 'Scan/type a serial…' },
  serialAbsentReason: { de: 'Grund (keine Seriennummer)', en: 'Reason (no serial)' },
  reasonNone: { de: '— hat Seriennummer —', en: '— has serial —' },
  reason_no_serial_printed: { de: 'Keine aufgedruckt', en: 'None printed' },
  reason_unreadable: { de: 'Unlesbar', en: 'Unreadable' },
  reason_sealed_unit: { de: 'Versiegelte Einheit', en: 'Sealed unit' },
  serialOrReasonRequired: {
    de: 'Jede Einheit braucht eine Seriennummer oder einen Grund.',
    en: 'Every unit needs a serial or a reason there is none.',
  },
  tagRequired: {
    de: 'Jede Einheit braucht ein Etikett aus dem Pool.',
    en: 'Every unit needs a label from the pool.',
  },
  dupTag: {
    de: 'Dasselbe Etikett ist zweimal gewählt — jede Einheit braucht ihr eigenes.',
    en: 'The same label is selected twice — each unit needs its own tag.',
  },
  dupSerial: {
    de: 'Dieselbe Seriennummer ist zweimal eingegeben.',
    en: 'The same serial is entered twice.',
  },
  submitUnits: { de: 'Einheiten annehmen', en: 'Receive units' },
  cancel: { de: 'Abbrechen', en: 'Cancel' },

  // ── the stockroom the goods land in ──
  fieldStockroom: { de: 'Lager', en: 'Stockroom' },
  selectStockroom: { de: '— Lager wählen —', en: '— select a stockroom —' },
  stockroomHint: {
    de: 'Wohin die Ware gelegt wird. "Auf Lager" ohne Ort ist kein Ort.',
    en: 'Where the goods are put. "In stock" with no place is not a place.',
  },
  stockroomRequired: { de: 'Ein Lager muss gewählt werden.', en: 'A stockroom has to be chosen.' },
  noStockroomTitle: { de: 'Kein Lager zum Einbuchen', en: 'No stockroom to receive into' },
  noStockrooms: {
    de: 'Angenommene Ware landet als "Auf Lager" in einem Lager. Ein Standort muss als Lager markiert sein, bevor Ware angenommen werden kann.',
    en: 'Received goods land in a stockroom as "in stock". A location has to be flagged as a stockroom before goods can be received.',
  },
  goLocations: { de: 'Zu den Standorten', en: 'Go to locations' },

  // ── outcomes + states ──
  successTitle: { de: 'Wareneingang {number} gebucht', en: 'Received on slip {number}' },
  queuedMsg: {
    de: 'Offline — der Wareneingang wird bei Verbindung automatisch gebucht.',
    en: 'Offline — the receipt will post automatically when back online.',
  },
  createdAssets: { de: 'Erstellte Assets ({count})', en: 'Created assets ({count})' },
  viewStockroom: { de: 'Im Lager anzeigen', en: 'View in stockroom' },

  readOnlyHint: {
    de: 'Zum Buchen des Wareneingangs ist die Wareneingangs-Schreibberechtigung nötig.',
    en: 'Receiving write permission is required to book goods in.',
  },

  emptyTitle: { de: 'Nichts anzunehmen', en: 'Nothing to receive' },
  // The queue table (design "A · Receiving queue", 2026-09-30).
  statOpenOrders: { de: 'Offene Bestellungen', en: 'Open orders' },
  statUnits: { de: 'Anzunehmende Einheiten', en: 'Units to receive' },
  statDueSoon: { de: 'Fällig in den nächsten 14 Tagen', en: 'Due in the next 14 days' },
  statStockroom: { de: 'Lager · {name}', en: 'Stockroom · {name}' },
  statStockrooms: { de: 'Lager · {codes}', en: 'Stockrooms · {codes}' },
  statStockroomsMore: { de: '+{n} weitere', en: '+{n} more' },
  statNoStockroom: { de: 'Kein Lager — Standorte öffnen', en: 'No stockroom — open Locations' },
  colOrder: { de: 'Bestellung', en: 'Order' },
  colActions: { de: 'Aktionen', en: 'Actions' },
  colItem: { de: 'Artikel', en: 'Item' },
  colRequest: { de: 'Antrag', en: 'Request' },
  colExpected: { de: 'Erwartet', en: 'Expected' },
  receivedOf: { de: '{received} von {qty} · noch {left}', en: '{received} of {qty} · {left} to go' },
  openRequest: { de: 'Antrag {number} öffnen', en: 'Open request {number}' },
  searchPlaceholder: { de: 'Bestellung, Lieferant, Modell suchen…', en: 'Search order, supplier, model…' },
  filterSupplier: { de: 'Alle Lieferanten', en: 'All suppliers' },
  noMatch: { de: 'Keine Position passt zum Filter.', en: 'No line matches the filter.' },
  receivedToast: {
    de: '{count} Einheit(en) angenommen — Beleg {number}.',
    en: '{count} unit(s) received — slip {number}.',
  },
  emptyMsg: {
    de: 'Es gibt keine Bestellungen mit offenen Positionen.',
    en: 'There are no purchase orders with outstanding lines.',
  },
  goOrders: { de: 'Bestellungen', en: 'Purchase Orders' },
  loadError: { de: 'Bestellungen konnten nicht geladen werden', en: 'Failed to load purchase orders' },
  // ⚠ Distinct from `noStockrooms`: "there is no stockroom" and "we could not ask"
  // are different facts, and only the first is a statement about the farm (§19).
  locationsError: { de: 'Standorte konnten nicht geladen werden', en: 'Failed to load locations' },
} satisfies Namespace;
